import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, catchError, Observable, of, shareReplay, switchMap, takeWhile, timer } from 'rxjs';
import { EnvironmentService } from '../../../../environments/environment.service';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { ELS_JOB_URL_STORAGE_KEY, ElsDeviceType, ElsJobStatistics } from './tado-dragino.models';

const QUERY_DATA_PERIOD = 5000;

@Injectable({
  providedIn: 'root',
})
export class TadoDraginoImportService {
  private readonly httpClient = inject(HttpClient);
  private readonly environment = inject(EnvironmentService);
  private readonly localStorageService = inject(LocalStorageService);

  private readonly elsImportUrl = `${this.environment.fleetService}/devices/els`;

  private readonly _uploading$ = new BehaviorSubject<boolean>(false);
  readonly uploading$ = this._uploading$.asObservable();
  private readonly _errorKey$ = new BehaviorSubject<string | null>(null);
  readonly errorKey$ = this._errorKey$.asObservable();

  private readonly _elsJobReportUrl$ = new BehaviorSubject<string | null>(this.localStorageService.getItem(ELS_JOB_URL_STORAGE_KEY));
  readonly elsJobReportUrl$ = this._elsJobReportUrl$.pipe(takeUntilDestroyed(), shareReplay(1));

  readonly elsJobStatistics$: Observable<ElsJobStatistics | null> = this.elsJobReportUrl$.pipe(
    switchMap((url) =>
      url
        ? timer(0, QUERY_DATA_PERIOD).pipe(
            switchMap(() => this.checkElsImportJobStatus(url)),
            takeWhile((response) => !!response?.pending, true),
          )
        : of(null),
    ),
    catchError((err) => {
      console.error(err.message);
      this.clearJobUrl();
      return of(null);
    }),
    takeUntilDestroyed(),
    shareReplay(1),
  );

  uploadElsFile(elsFile: File, deviceType: ElsDeviceType): void {
    this._uploading$.next(true);
    this.importElsDocument(elsFile, deviceType).subscribe({
      next: (response) => {
        this._uploading$.next(false);
        this.startMonitoring(response.url);
      },
      error: (err) => {
        this._uploading$.next(false);
        console.error(err.message);
        this.setErrorKey('system.errors.somethingWentWrong');
      },
    });
  }

  private importElsDocument(file: File, deviceType: ElsDeviceType): Observable<{ url: string }> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('deviceType', deviceType);

    return this.httpClient.post<{ url: string }>(this.elsImportUrl, fd);
  }

  checkElsImportJobStatus(url: string): Observable<ElsJobStatistics> {
    return this.httpClient.get<ElsJobStatistics>(url);
  }

  clearJobUrl(): void {
    this._elsJobReportUrl$.next(null);
    this.localStorageService.removeItem(ELS_JOB_URL_STORAGE_KEY);
  }

  setErrorKey(errorKey: string | null): void {
    this._errorKey$.next(errorKey);
  }

  private startMonitoring(elsJobUrl: string): void {
    this.localStorageService.setItem(ELS_JOB_URL_STORAGE_KEY, elsJobUrl);
    this._elsJobReportUrl$.next(elsJobUrl);
  }
}
